import * as d3Zoom from "d3-zoom";
import { PinRequest } from "@hokuto/jam-core";
import {
  ElementMouseEvent,
  ElementObserverProps,
  JamAtlasParams,
  JamCanvasDrawerStatus,
  JamHandlerType,
  JamMapParams,
  JamParamsChangeProps,
  JamParamsMouseEvent,
  JamParamsObserverProps,
  ZoomControlsSchema,
} from "../../types.js";
import { ANIMATION_DURATION } from "../../const.js";
import { ElementObserver } from "./element.js";
import { debounce } from "../debounce.js";
import { MapApi } from "../../api/map.js";
import { AtlasApi } from "../../api/atlas.js";

export type ParamsObserverActionType = keyof Pick<
  JamParamsObserverProps,
  "onError" | "onDraw" | "onChange" | "onClick" | "onMove" | "onLeave"
>;

export type ParamsObserverAction<K extends ParamsObserverActionType> =
  Required<JamParamsObserverProps>[K];

export type ParamsCallbackHandler<K extends ParamsObserverActionType> =
  JamHandlerType<Parameters<ParamsObserverAction<K>>, void>;

export class ParamsObserver {
  onError: JamParamsObserverProps["onError"];
  onDraw: JamParamsObserverProps["onDraw"];
  onChange: ElementObserverProps<HTMLCanvasElement>["onChange"];
  onClick: ElementObserverProps<HTMLCanvasElement>["onClick"];
  onMove: ElementObserverProps<HTMLCanvasElement>["onMove"];
  onLeave: ElementObserverProps<HTMLCanvasElement>["onLeave"];
  waitChange?: number;
  waitClick?: number;
  waitMove?: number;
  waitLeave?: number;
  mapParams?: JamMapParams;
  atlasParams?: JamAtlasParams;
  controls?: ZoomControlsSchema<HTMLCanvasElement>;
  changeHandler?: ParamsCallbackHandler<"onChange">;
  elementObserver?: ElementObserver<HTMLCanvasElement>;
  clickHandler?: ParamsCallbackHandler<"onClick">;
  moveHandler?: ParamsCallbackHandler<"onMove">;
  leaveHandler?: ParamsCallbackHandler<"onLeave">;
  transformationHandler?: ReturnType<
    typeof ParamsObserver.getTransformationHandler
  >;

  constructor(args: JamParamsObserverProps = {}) {
    const { mapParams, atlasParams, controls, ...props } = args;
    this.mapParams = mapParams;
    this.atlasParams = atlasParams;

    this.setListeners(props);
    this.setControls(controls);
  }

  setControls(controls?: ZoomControlsSchema<HTMLCanvasElement>) {
    this.controls = controls;
    this.transformationHandler =
      ParamsObserver.getTransformationHandler(controls);
    this.setListeners();
    return this;
  }

  setParams(params: JamParamsChangeProps) {
    const { mapParams, atlasParams } = { ...this, ...params };
    this.mapParams = mapParams;
    this.atlasParams = atlasParams;
    if (!this.changeHandler?.hasCallback) return this;
    this.changeHandler(params);
    return this;
  }

  sendError(error: Error) {
    if (!this.onError) return this;
    this.onError(error);
    return this;
  }

  sendDraw(status: JamCanvasDrawerStatus) {
    if (!this.onDraw) return this;
    this.onDraw(status);
    return this;
  }

  setListeners(
    args: Pick<
      JamParamsObserverProps,
      | "onError"
      | "onDraw"
      | "onChange"
      | "waitChange"
      | "onClick"
      | "waitClick"
      | "onMove"
      | "waitMove"
      | "onLeave"
      | "waitLeave"
    > = {}
  ) {
    const {
      onError,
      onDraw,
      onChange,
      onClick,
      onMove,
      onLeave,
      waitChange,
      waitClick,
      waitMove,
      waitLeave,
    } = {
      ...this,
      ...args,
    };
    this.onError = onError;
    this.onDraw = onDraw;
    this.onChange = onChange;
    this.onClick = onClick;
    this.onMove = onMove;
    this.onLeave = onLeave;
    this.waitChange = waitChange;
    this.waitClick = waitClick;
    this.waitMove = waitMove;
    this.waitLeave = waitLeave;

    this.elementObserver && this.elementObserver.removeListeners();

    this.changeHandler = debounce(onChange, waitChange);

    if (!this.controls?.element) return this;

    this.clickHandler = ParamsObserver.getMouseEventHandler(this, this.onClick);
    this.moveHandler = ParamsObserver.getMouseEventHandler(this, this.onMove);
    this.leaveHandler = ParamsObserver.getMouseEventHandler(this, this.onLeave);
    this.elementObserver = new ElementObserver({
      element: this.controls?.element,
      onClick: this.clickHandler,
      onMove: this.moveHandler,
      onLeave: this.leaveHandler,
      waitClick: this.waitClick,
      waitMove: this.waitMove,
      waitLeave: this.waitLeave,
    });
    return this;
  }

  navigatePin(pin: PinRequest, duration = ANIMATION_DURATION) {
    const { transformationHandler, mapParams, atlasParams } = this;
    if (!transformationHandler?.hasCallback) return this;
    const req = mapParams
      ? () => {
          const { serverUrl, token, projection, mapId, scale } = mapParams;
          let { width, height } = mapParams;
          width /= scale;
          height /= scale;
          return MapApi.Pin({
            serverUrl,
            token,
            mapId,
            projection,
            width,
            height,
            pin,
          });
        }
      : atlasParams
      ? () => {
          const { serverUrl, token, projection, atlasId, atlas, scale } =
            atlasParams;
          let { width, height } = atlasParams;
          width /= scale;
          height /= scale;
          return AtlasApi.Pin({
            serverUrl,
            token,
            projection,
            atlasId,
            atlas,
            width,
            height,
            pin,
          });
        }
      : undefined;
    if (!req) return this;

    req().then((viewbox) => {
      transformationHandler(
        new d3Zoom.ZoomTransform(
          viewbox.scale,
          viewbox.margin.left,
          viewbox.margin.top
        ),
        duration
      );
    });
    return this;
  }

  static getMouseEventHandler(
    args: ParamsObserver,
    callback?: JamHandlerType<[JamParamsMouseEvent], void>
  ): JamHandlerType<[ElementMouseEvent], void> {
    function handler(eventProps: JamParamsMouseEvent) {
      const { mapParams, atlasParams } = args;
      const props = {
        ...eventProps,
        mapParams: mapParams && {
          ...mapParams,
          point: eventProps.point,
        },
        atlasParams: atlasParams && {
          ...atlasParams,
          point: eventProps.point,
        },
      };
      return callback && callback(props);
    }

    handler.hasCallback = !!callback;
    return handler;
  }

  static transformElement<T extends Element>(args: {
    controls: ZoomControlsSchema<T>;
    transformation: d3Zoom.ZoomTransform;
    duration?: number;
  }) {
    const { controls, transformation, duration } = args;
    const currentSize = controls.element.getBoundingClientRect();
    const diffX = (currentSize.width - controls.initialSize.width) / 2;
    const diffY = (currentSize.height - controls.initialSize.height) / 2;

    const value = new d3Zoom.ZoomTransform(
      transformation.k,
      transformation.x + diffX,
      transformation.y + diffY
    );

    controls.transform({ value, duration });
  }

  static getTransformationHandler<T extends Element>(
    controls?: ZoomControlsSchema<T>
  ): JamHandlerType<[d3Zoom.ZoomTransform, number | undefined], void> {
    function handler(transformation: d3Zoom.ZoomTransform, duration?: number) {
      if (!controls) return;
      ParamsObserver.transformElement({
        controls,
        transformation,
        duration,
      });
    }
    handler.hasCallback = !!controls;
    return handler;
  }
}
